import { Search, BookOpen, Bookmark, StickyNote, Layers, Info, X, Printer, Download, Settings, Plus, BookOpenCheck } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import type { SidebarTab, Bookmark as BookmarkType, Note, TOCItem, SearchResult, BookMetadata, NavigationCallbacks, ReaderActions, BookFormat } from './types';
import SearchPanel from './panels/SearchPanel';
import TOCPanel from './panels/TOCPanel';
import BookmarksPanel from './panels/BookmarksPanel';
import NotesPanel from './panels/NotesPanel';
import FlashcardPanel from './panels/FlashcardPanel';
import InfoPanel from './panels/InfoPanel';
import PrintPanel from './panels/PrintPanel';
import DownloadPanel from './panels/DownloadPanel';
import SettingsPanel from './panels/SettingsPanel';
import QuickActionMenu from './QuickActionMenu';
import LayoutPanel from './panels/LayoutPanel';

interface ReaderSidebarProps {
  activeTab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;
  tableOfContents?: TOCItem[];
  bookmarks?: BookmarkType[];
  notes?: Note[];
  metadata: BookMetadata;
  format: BookFormat;
  currentLocation: string | number;
  currentLocationLabel: string;
  navigationCallbacks: NavigationCallbacks;
  actions: ReaderActions;
  onStartFlashcardStudy?: (deckId: string) => void;
  onAddPageNote?: (noteText: string, color: import('./types').HighlightColor) => void;
  hideTabsNav?: boolean; // Hide vertical icon tabs (for mobile BottomSheet)
  currentPage?: number;
  totalPages?: number;
  /**
   * SecureContentChunkId for the chapter/page currently being read. Passed
   * down to FlashcardPanel so the Library tab can auto-scope to the user's
   * current position. `null` when the renderer cannot (yet) resolve a chunk
   * Guid for the active location.
   */
  activeSecureContentChunkId?: string | null;
}

const TAB_IDS = [
  { id: 'search' as const, icon: Search, key: 'App.Reader.Panel.Search' },
  { id: 'toc' as const, icon: BookOpen, key: 'App.Reader.Panel.Contents' },
  { id: 'bookmarks' as const, icon: Bookmark, key: 'App.Reader.Panel.Bookmarks' },
  { id: 'notes' as const, icon: StickyNote, key: 'App.Reader.Panel.Notes' },
  { id: 'flashcards' as const, icon: Layers, key: 'App.Reader.Panel.Flashcards' },
  { id: 'print' as const, icon: Printer, key: 'App.Reader.Panel.Print' },
  { id: 'download' as const, icon: Download, key: 'App.Reader.Panel.Download' },
  { id: 'info' as const, icon: Info, key: 'App.Reader.Panel.Info' },
  { id: 'layout' as const, icon: BookOpenCheck, key: 'App.Reader.Panel.Layout' },
  { id: 'settings' as const, icon: Settings, key: 'App.Reader.Panel.Settings' },
  { id: 'quickAction' as const, icon: Plus, key: 'App.Reader.Panel.QuickActions' },
];

function ReaderSidebar({
  activeTab,
  onTabChange,
  tableOfContents = [],
  bookmarks = [],
  notes = [],
  metadata,
  format,
  currentLocation,
  currentLocationLabel,
  navigationCallbacks,
  actions,
  onStartFlashcardStudy,
  onAddPageNote,
  hideTabsNav = false,
  currentPage,
  totalPages,
  activeSecureContentChunkId = null,
}: ReaderSidebarProps) {
  const { t } = useTranslation();
  const tabs = TAB_IDS.map((tab) => ({ ...tab, label: t(tab.key) }));
  const renderPanel = () => {
    switch (activeTab) {
      case 'search':
        return (
          <SearchPanel
            onSearch={actions.onSearch}
            onNavigateToResult={(result) => {
              navigationCallbacks.onNavigateToSearchResult(result);
              onTabChange(null); // Close the search panel after navigation
            }}
          />
        );
      case 'toc':
        return (
          <TOCPanel
            items={tableOfContents}
            onNavigate={navigationCallbacks.onNavigateToTOC}
            currentLocation={currentLocation}
          />
        );
      case 'bookmarks':
        return (
          <BookmarksPanel
            bookmarks={bookmarks}
            onNavigate={navigationCallbacks.onNavigateToBookmark}
            onDelete={actions.onDeleteBookmark}
            onUpdate={actions.onUpdateBookmark}
            onCreateBookmark={actions.onCreateBookmark}
            currentLocation={currentLocation}
            currentLocationLabel={currentLocationLabel}
          />
        );
      case 'notes':
        return (
          <NotesPanel
            notes={notes}
            currentLocation={currentLocation}
            currentLocationLabel={currentLocationLabel}
            onNavigate={navigationCallbacks.onNavigateToNote}
            onDelete={actions.onDeleteNote}
            onUpdate={actions.onUpdateNote}
            onAddPageNote={onAddPageNote}
          />
        );
      case 'flashcards':
        return (
          <FlashcardPanel
            bookId={metadata.id}
            currentLocation={currentLocation}
            currentLocationLabel={currentLocationLabel}
            onStartStudy={onStartFlashcardStudy || (() => {})}
            secureContentChunkId={activeSecureContentChunkId}
          />
        );
      case 'print':
        return (
          <PrintPanel
            bookId={metadata.id}
            totalPages={metadata.pageCount || 0}
            format={format}
          />
        );
      case 'download':
        return (
          <DownloadPanel
            bookId={metadata.id}
            totalPages={metadata.pageCount || 0}
            format={format}
          />
        );
      case 'info':
        return <InfoPanel metadata={metadata} format={format} />;
      case 'layout':
        return (
          <LayoutPanel
            isOpen={true}
            onClose={() => onTabChange(null)}
            asPanel={true}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        );
      case 'settings':
        return (
          <SettingsPanel
            isOpen={true}
            onClose={() => onTabChange(null)}
            asPanel={true}
          />
        );
      case 'quickAction':
        return (
          <QuickActionMenu
            isOpen={true}
            onClose={() => onTabChange(null)}
            onAddNote={onAddPageNote || (() => {})}
            onAddBookmark={actions.onCreateBookmark}
            currentLocation={currentLocation}
            currentLocationLabel={currentLocationLabel}
            asPanel={true}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={hideTabsNav ? "h-full bg-white" : "flex h-full bg-white border-r border-neutral-200"}>
      {/* Icon Tabs (Vertical) - Hidden on mobile when in BottomSheet */}
      {!hideTabsNav && (
        <div
          role="tablist"
          aria-label={t('App.Reader.PanelsAriaLabel')}
          aria-orientation="vertical"
          className="w-16 h-full bg-neutral-50 border-r border-neutral-200 flex flex-col items-center py-4 gap-2 overflow-y-auto"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(isActive ? null : tab.id)}
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                aria-controls={`panel-${tab.id}`}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    const tabButtons = e.currentTarget.parentElement?.querySelectorAll('[role="tab"]');
                    if (!tabButtons || tabButtons.length === 0) return;
                    const currentIndex = Array.from(tabButtons).indexOf(e.currentTarget);
                    const nextIndex = e.key === 'ArrowDown'
                      ? (currentIndex + 1) % tabButtons.length
                      : (currentIndex - 1 + tabButtons.length) % tabButtons.length;
                    (tabButtons[nextIndex] as HTMLElement).focus();
                  }
                }}
                className={`
                  w-12 h-12 flex-shrink-0 flex flex-col items-center justify-center rounded-lg transition-colors
                  ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800'
                  }
                `}
                title={tab.label}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-neutral-600'}`} />
                <span className="text-[10px] mt-0.5">{tab.label.slice(0, 4)}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Panel Content */}
      {activeTab && (
        <div className={hideTabsNav ? "h-full flex flex-col" : "flex-1 w-80 flex flex-col"}>
          {/* Close Button - Always visible when panel is open */}
          {!hideTabsNav && (
            <div className="p-2 border-b border-neutral-200 flex justify-end">
              <button
                onClick={() => onTabChange(null)}
                className="p-2 rounded-lg text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 transition-colors"
                aria-label={t('App.Reader.ClosePanel')}
                title={t('App.Reader.ClosePanel')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Panel */}
          <div className="flex-1 min-h-0 overflow-hidden">{renderPanel()}</div>
        </div>
      )}
    </div>
  );
}

export default ReaderSidebar;
